import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "triptap.pwa.installdismissed";

/**
 * PWA install nudge. Chrome/Edge/Android fire `beforeinstallprompt` — we
 * capture that event, show a small dockable card on the bottom-left, and let
 * the user tap Install (which surfaces the native install sheet). Dismissed
 * choice is remembered for 30 days so we don't nag.
 *
 * iOS Safari doesn't fire the event — for iPhone users we detect the platform
 * and show a "Add to Home Screen" hint instead, since that's the equivalent
 * install path there.
 */
export default function InstallPrompt() {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // If the user already installed the PWA, or dismissed recently, stay out.
    try {
      const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissed && Date.now() - dismissed < 30 * 24 * 60 * 60 * 1000) return;
      if (window.matchMedia?.("(display-mode: standalone)")?.matches) return;
      if (window.navigator.standalone) return; // iOS installed check
    } catch { /* no-op */ }

    const ua = (window.navigator.userAgent || "").toLowerCase();
    const looksIos = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
    if (looksIos) {
      setIsIOS(true);
      // Wait a few seconds so we don't ambush a first-time visitor.
      const t = setTimeout(() => setVisible(true), 6000);
      return () => clearTimeout(t);
    }

    const onBip = (e) => {
      e.preventDefault();
      setEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const install = async () => {
    if (!event) return;
    try {
      event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === "accepted") setVisible(false);
    } catch { /* browser rejected */ }
    setEvent(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Triptap"
      className="fixed bottom-4 left-4 md:max-w-sm z-[9998]"
      data-testid="pwa-install-prompt"
    >
      <div className="glass-card gold-border rounded-2xl p-4 shadow-2xl bg-[#0b1220]/95 border border-white/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-700 grid place-items-center flex-shrink-0 shadow-lg">
            <span className="font-display font-black text-white text-lg">T</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-white text-sm">Install Triptap</div>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              {isIOS
                ? <>Tap <b className="text-red-300">Share</b> then <b className="text-red-300">Add to Home Screen</b> to re-book in one tap.</>
                : "Add it to your home screen for one-tap re-booking, offline start, and no browser bar."}
            </p>
            <div className="flex gap-2 mt-3">
              {!isIOS && (
                <button
                  onClick={install}
                  className="btn-gold rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5"
                  data-testid="pwa-install-btn"
                >
                  <Download className="w-3 h-3" /> Install
                </button>
              )}
              <button
                onClick={dismiss}
                className="rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wide bg-transparent border border-white/10 text-slate-400 hover:text-white"
                data-testid="pwa-install-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} className="text-slate-500 hover:text-white flex-shrink-0" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
